import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CheckCircle2, ChevronDown, ChevronUp, Edit, Plus, RefreshCw, Trash2 } from 'lucide-react';

import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { useAuth } from '../../context/AuthContext';
import AiPromptConfigService, {
  isPlaceholderPrompt,
} from '../../services/aiPromptConfigService';
import {
  BUILDER_SLOT_OPTIONS,
  DEFAULT_MUNICIPAL_SECTIONS,
  canManageAiPromptConfig,
  createEmptySectionRow,
  formatSectionMappingSummary,
  humanizeSectionKey,
  resolveSectionDefinitions,
  validateSectionDefinitions,
} from '../../utils/aiPromptConfigUtils';

const EMPTY_FORM = {
  _id: '',
  promptName: '',
  promptContent: '',
  sections: [],
};

const mapPromptToForm = (item) => ({
  _id: item._id,
  promptName: item.promptName || '',
  promptContent: item.promptContent || '',
  sections: resolveSectionDefinitions(item).length
    ? resolveSectionDefinitions(item)
    : [...DEFAULT_MUNICIPAL_SECTIONS],
});

const AiPromptConfig = () => {
  const { user } = useAuth();
  const { success, error: showError, toasts, removeToast } = useToast();
  const canManage = canManageAiPromptConfig(user);

  const [prompts, setPrompts] = useState([]);
  const [hasPlaceholderOnly, setHasPlaceholderOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const liveSectionValidation = useMemo(
    () => validateSectionDefinitions(form.sections),
    [form.sections],
  );

  const loadPrompts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AiPromptConfigService.getPrompts();
      setPrompts(Array.isArray(res.data) ? res.data : []);
      setHasPlaceholderOnly(Boolean(res.hasPlaceholderOnly));
    } catch (err) {
      showError(err.message || 'Failed to load AI prompt configurations.');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    loadPrompts();
  }, [loadPrompts]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditorOpen(false);
  };

  const onCreate = () => {
    if (!canManage) {
      showError('You do not have permission to create AI prompt configurations.');
      return;
    }
    setForm({
      ...EMPTY_FORM,
      sections: DEFAULT_MUNICIPAL_SECTIONS.map((section) => ({ ...section })),
    });
    setEditorOpen(true);
  };

  const onCreateFromTemplate = (item) => {
    if (!canManage) {
      showError('You do not have permission to create AI prompt configurations.');
      return;
    }
    const source = item || AiPromptConfigService.getPlaceholderTemplate();
    setForm({
      _id: '',
      promptName: `${source.promptName} (Copy)`,
      promptContent: source.promptContent || '',
      sections: resolveSectionDefinitions(source).length
        ? resolveSectionDefinitions(source).map((section) => ({ ...section }))
        : DEFAULT_MUNICIPAL_SECTIONS.map((section) => ({ ...section })),
    });
    setEditorOpen(true);
  };

  const onEdit = (item) => {
    if (!canManage) {
      showError('You do not have permission to edit AI prompt configurations.');
      return;
    }
    if (isPlaceholderPrompt(item)) {
      onCreateFromTemplate(item);
      return;
    }
    setForm(mapPromptToForm(item));
    setEditorOpen(true);
  };

  const updateSection = (index, patch) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections.map((row, i) =>
        i === index ? { ...row, ...patch } : row,
      ),
    }));
  };

  const addSection = () => {
    setForm((prev) => ({
      ...prev,
      sections: [
        ...prev.sections,
        createEmptySectionRow(prev.sections.length + 1),
      ],
    }));
  };

  const removeSection = (index) => {
    setForm((prev) => ({
      ...prev,
      sections: prev.sections
        .filter((_, i) => i !== index)
        .map((row, i) => ({ ...row, order: i + 1 })),
    }));
  };

  const moveSection = (index, direction) => {
    setForm((prev) => {
      const next = [...prev.sections];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return {
        ...prev,
        sections: next.map((row, i) => ({ ...row, order: i + 1 })),
      };
    });
  };

  const onSave = async () => {
    if (!canManage) {
      showError('You do not have permission to save AI prompt configurations.');
      return;
    }

    if (!form.promptName.trim()) {
      showError('Prompt Name is required.');
      return;
    }

    if (!form.promptContent.trim()) {
      showError('Prompt Content is required.');
      return;
    }

    const sectionValidation = validateSectionDefinitions(form.sections);

    if (!sectionValidation.valid) {
      showError(sectionValidation.errors.join(' '));
      return;
    }

    if (sectionValidation.warnings.length) {
      const proceed = window.confirm(
        `Section warnings:\n\n${sectionValidation.warnings.join('\n')}\n\nSave anyway?`,
      );
      if (!proceed) return;
    }

    const payload = {
      promptName: form.promptName.trim(),
      promptContent: form.promptContent,
      sectionMapping: sectionValidation.sections,
      isActive:
        !form._id && hasPlaceholderOnly
          ? true
          : prompts.find((p) => p._id === form._id)?.isActive || false,
    };

    setSaving(true);
    try {
      if (form._id) {
        await AiPromptConfigService.updatePrompt(form._id, payload);
        success('Prompt updated.');
      } else {
        await AiPromptConfigService.createPrompt(payload);
        success('Prompt created.');
      }
      resetForm();
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Failed to save prompt.');
    } finally {
      setSaving(false);
    }
  };

  const onActivate = async (item) => {
    if (!canManage) {
      showError('You do not have permission to activate prompts.');
      return;
    }

    try {
      await AiPromptConfigService.activatePrompt(item._id);
      success('Prompt activated for AI engine injection.');
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Activation failed.');
    }
  };

  const onDelete = async (item) => {
    if (!canManage) {
      showError('You do not have permission to delete prompts.');
      return;
    }

    const confirmed = window.confirm(`Delete prompt "${item.promptName}"?`);
    if (!confirmed) return;

    try {
      await AiPromptConfigService.deletePrompt(item._id);
      success('Prompt deleted.');
      loadPrompts();
    } catch (err) {
      showError(err.message || 'Delete failed.');
    }
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="AI Prompt Configuration Screen"
        description="Configure reusable prompts and activate one for dynamic LLM request injection"
        actions={[
          {
            icon: (
              <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
            ),
            variant: 'outline',
            onClick: loadPrompts,
          },
          ...(canManage
            ? [
                {
                  label: 'Create',
                  icon: <Plus size={16} />,
                  variant: 'primary',
                  onClick: onCreate,
                },
              ]
            : []),
        ]}
      />

      <div className="page-container">
        <div
          className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          style={{ lineHeight: 1.6 }}
        >
          <strong>Important:</strong> Section changes apply to{' '}
          <strong>new AI RFPs only</strong>. Each RFP stores a snapshot (keys, labels, builder slots) at creation.
          {hasPlaceholderOnly && (
            <span>
              {' '}
              You are viewing a <strong>sample template</strong> because no saved prompts exist yet.
              Use <strong>Create</strong> or <strong>Create from template</strong> to save a real prompt, then activate it.
            </span>
          )}
          {!canManage && (
            <span>
              {' '}
              You have read-only access. Contact an administrator to edit prompts.
            </span>
          )}
        </div>

        {editorOpen && canManage && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 mb-6">
            <h3 className="text-base font-semibold text-gray-800 mb-4">
              {form._id ? 'Edit Prompt' : 'Create Prompt'}
            </h3>

            <div className="grid grid-cols-1 gap-4 mb-4">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                  Prompt Name
                </label>
                <input
                  value={form.promptName}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, promptName: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-lg p-2.5 text-sm"
                  placeholder="Prompt Name"
                />
              </div>
            </div>

            <div className="mb-4">
              <div className="d-flex align-items-center justify-content-between mb-2">
                <label className="block text-xs font-bold text-gray-500 uppercase mb-0">
                  Section Definitions
                </label>
                <button
                  type="button"
                  onClick={addSection}
                  className="btn btn-outline-primary btn-sm"
                  style={{ fontSize: '12px' }}
                >
                  <Plus size={14} className="me-1" />
                  Add Section
                </button>
              </div>
              <p className="text-gray-500 text-xs mb-3">
                Each section has a camelCase key, display label, generation order, and builder slot for the EOI template.
              </p>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-2 px-3 text-left">#</th>
                      <th className="py-2 px-3 text-left">Key</th>
                      <th className="py-2 px-3 text-left">Label</th>
                      <th className="py-2 px-3 text-left">Builder Slot</th>
                      <th className="py-2 px-3 text-left">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.sections.map((section, index) => (
                      <tr key={`section-row-${index}`} className="border-t">
                        <td className="py-2 px-3 align-middle">{index + 1}</td>
                        <td className="py-2 px-3">
                          <input
                            value={section.key}
                            onChange={(e) => {
                              const key = e.target.value;
                              updateSection(index, {
                                key,
                                label: section.label || humanizeSectionKey(key),
                              });
                            }}
                            className="w-full border border-gray-300 rounded p-2 text-sm"
                            placeholder="scopeOfWork"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <input
                            value={section.label}
                            onChange={(e) =>
                              updateSection(index, { label: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded p-2 text-sm"
                            placeholder="Scope of Work"
                          />
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={section.builderSlot || 'document_overflow'}
                            onChange={(e) =>
                              updateSection(index, { builderSlot: e.target.value })
                            }
                            className="w-full border border-gray-300 rounded p-2 text-sm"
                          >
                            {BUILDER_SLOT_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>
                                {opt.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3">
                          <div className="d-flex gap-1">
                            <button
                              type="button"
                              title="Move up"
                              disabled={index === 0}
                              onClick={() => moveSection(index, -1)}
                              className="p-1 border rounded"
                            >
                              <ChevronUp size={14} />
                            </button>
                            <button
                              type="button"
                              title="Move down"
                              disabled={index === form.sections.length - 1}
                              onClick={() => moveSection(index, 1)}
                              className="p-1 border rounded"
                            >
                              <ChevronDown size={14} />
                            </button>
                            <button
                              type="button"
                              title="Remove"
                              onClick={() => removeSection(index)}
                              className="p-1 border border-red-200 text-red-600 rounded"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {!liveSectionValidation.valid && (
                <div className="text-red-600 text-xs mt-2">
                  {liveSectionValidation.errors.map((msg) => (
                    <div key={msg}>{msg}</div>
                  ))}
                </div>
              )}
              {liveSectionValidation.warnings.length > 0 && (
                <div className="text-amber-700 text-xs mt-2">
                  {liveSectionValidation.warnings.map((msg) => (
                    <div key={msg}>{msg}</div>
                  ))}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-gray-500 uppercase mb-2">
                Prompt Content
              </label>
              <textarea
                value={form.promptContent}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    promptContent: e.target.value,
                  }))
                }
                className="w-full border border-gray-300 rounded-lg p-3 text-sm min-h-[160px]"
                placeholder="Write the base AI prompt instructions and section-specific generation rules..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={resetForm}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || !liveSectionValidation.valid}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {saving ? 'Saving...' : form._id ? 'Edit' : 'Create'}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-3 px-4">Prompt Name</th>
                  <th className="py-3 px-4">Prompt Content</th>
                  <th className="py-3 px-4">Sections</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {prompts.map((item) => {
                  const placeholder = isPlaceholderPrompt(item);
                  return (
                  <tr key={item._id} className="border-b align-top">
                    <td className="py-3 px-4 font-semibold text-gray-900">
                      <div className="flex items-center gap-2">
                        <span>{item.promptName}</span>
                        {placeholder && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-amber-100 text-amber-800 font-bold uppercase">
                            Sample
                          </span>
                        )}
                        {item.isActive && !placeholder && (
                          <span className="px-2 py-0.5 text-[10px] rounded-full bg-green-100 text-green-700 font-bold uppercase">
                            Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700 max-w-[420px]">
                      <div className="line-clamp-3">{item.promptContent}</div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {formatSectionMappingSummary(item.sectionMapping)}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        {canManage && (
                          <>
                            {placeholder ? (
                              <button
                                title="Create from template"
                                onClick={() => onCreateFromTemplate(item)}
                                className="px-2 py-1 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50 text-xs font-semibold"
                              >
                                Use template
                              </button>
                            ) : (
                              <>
                                <button
                                  title="Edit"
                                  onClick={() => onEdit(item)}
                                  className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                                >
                                  <Edit size={14} />
                                </button>
                                <button
                                  title="Activate"
                                  onClick={() => onActivate(item)}
                                  className="p-2 rounded-md border border-gray-200 hover:bg-gray-50"
                                >
                                  <CheckCircle2 size={14} />
                                </button>
                                <button
                                  title="Delete"
                                  onClick={() => onDelete(item)}
                                  className="p-2 rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </>
                            )}
                          </>
                        )}
                        {!canManage && (
                          <span className="text-xs text-gray-500">View only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
                })}
                {!prompts.length && (
                  <tr>
                    <td colSpan={4} className="py-5 px-4 text-sm text-gray-500">
                      No prompt configurations found. Create one and activate it
                      for AI flow injection.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AiPromptConfig;
